/* MAX AUTO local estimates v1 — arithmetic and preference policy; no Shopify writes. */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.MaxCurrencyCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const DAY = 86400000;
  const POLICY = Object.freeze({ fresh: DAY, expire: 3 * DAY, preference: 30 * DAY, destination: 7 * DAY });
  const normalize = value => String(value || '').normalize('NFKC').replace(/\s+/g, ' ').trim().toLowerCase();
  const validCode = value => /^[A-Z]{3}$/.test(value || '');
  const validMinor = value => Number.isSafeInteger(value) && value >= 0 && value <= 1e12;

  function parseUSD(text) {
    // Only the audited USD money layout is accepted. Never strip arbitrary characters into a price.
    const value = String(text || '').replace(/\u00a0/g, ' ').trim();
    const match = /^(?:USD\s*\$?|US\$|\$)\s*((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?)(?:\s*USD)?$/.exec(value);
    if (!match) return null;
    const minor = Math.round(Number(match[1].replace(/,/g, '')) * 100);
    return validMinor(minor) ? minor : null;
  }

  function countryCode(value, data) {
    const raw = String(value || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(raw) && Object.hasOwn(data.countries, raw)) return raw;
    return data.aliases[normalize(value)] || '';
  }

  function hydrate(raw, headerCountry, now = Date.now()) {
    let stored = raw;
    if (typeof raw === 'string') { try { stored = JSON.parse(raw); } catch { stored = null; } }
    const recent = stored && stored.v === 1 && Number.isFinite(stored.at) && now - stored.at >= -300000 && now - stored.at < POLICY.preference;
    const state = recent ? { ...stored } : { v: 1, mode: 'auto', currency: 'USD', destination: '', destinationAt: 0, at: now };
    if (!['auto', 'manual'].includes(state.mode) || !validCode(state.currency)) {
      state.mode = 'auto'; state.currency = 'USD';
    }
    if (state.headerCountry && state.headerCountry !== headerCountry) {
      state.destination = ''; state.destinationAt = 0;
    }
    if (!Number.isFinite(state.destinationAt) || state.destinationAt > now + 300000 || now - state.destinationAt >= POLICY.destination) state.destination = '';
    state.headerCountry = headerCountry;
    return state;
  }

  function select(state, value, now = Date.now()) {
    if (value !== 'AUTO' && !validCode(value)) return state;
    return { ...state, mode: value === 'AUTO' ? 'auto' : 'manual', currency: value === 'AUTO' ? state.currency : value, at: now };
  }

  function destination(state, country, now = Date.now()) {
    return { ...state, destination: country, destinationAt: country ? now : 0, at: now };
  }

  function resolve(state, context, data) {
    if (context.baseCurrency !== 'USD' || context.activeCurrency !== 'USD') return { currency: 'USD', source: 'blocked', country: '' };
    if (state.mode === 'manual') return { currency: state.currency, source: 'manual', country: '' };
    const country = countryCode(context.destination || state.destination || context.headerCountry, data);
    const currency = data.countries[country];
    return { currency: validCode(currency) ? currency : 'USD', source: country && currency ? (context.destination || state.destination ? 'destination' : 'country') : 'unknown', country };
  }

  function validateRates(raw, now = Date.now()) {
    if (!raw || raw.result !== 'success' || raw.base_code !== 'USD' || raw.rates?.USD !== 1) throw new Error('INVALID_FX_BASE');
    const updated = raw.time_last_update_unix * 1000;
    const next = raw.time_next_update_unix * 1000;
    if (!Number.isFinite(updated) || updated < 1e12 || updated > now + 300000 || !Number.isFinite(next) || next <= updated) throw new Error('INVALID_FX_TIME');
    const rates = Object.create(null);
    for (const [code, rate] of Object.entries(raw.rates)) {
      if (validCode(code) && typeof rate === 'number' && Number.isFinite(rate) && rate > 0 && rate < 1e9) rates[code] = rate;
    }
    return { updated, next: Math.min(next, updated + POLICY.fresh), rates };
  }

  function freshness(snapshot, now = Date.now()) {
    if (!snapshot || !Number.isFinite(snapshot.updated) || snapshot.updated > now + 300000) return 'unavailable';
    const age = now - snapshot.updated;
    if (age >= POLICY.expire) return 'expired';
    return age >= POLICY.fresh ? 'stale' : 'fresh';
  }

  function convert(minor, currency, snapshot, now = Date.now()) {
    if (!validMinor(minor) || !validCode(currency)) return null;
    if (currency === 'USD') return minor / 100;
    if (!['fresh', 'stale'].includes(freshness(snapshot, now))) return null;
    const rate = snapshot.rates[currency];
    if (!Number.isFinite(rate) || rate <= 0) return null;
    const converted = minor / 100 * rate;
    return Number.isFinite(converted) && converted < Number.MAX_SAFE_INTEGER ? converted : null;
  }

  function format(amount, code, locale = 'en-US') {
    if (!Number.isFinite(amount) || amount < 0 || !validCode(code)) return '';
    let number;
    try { number = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(amount); }
    catch { number = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(amount); }
    return `${code} ${number}`;
  }

  function readQuote(href, context) {
    try {
      const url = new URL(href);
      if (url.origin !== 'https://account.maxkauto.com' || url.pathname !== '/account') return null;
      const s = JSON.parse(url.searchParams.get('selection'));
      if (!s || !validMinor(s.displayedTotalMinor) || s.displayedTotalMinor <= 0 || !['C&F', 'FOB'].includes(s.displayedBasis)) return null;
      if (String(s.variantId) !== String(context.variantId) || s.handle !== context.handle || !context.country || !context.port || s.country !== context.country || s.port !== context.port || !s.method || !context.methodReady) return null;
      const ready = s.displayedBasis === 'C&F' ? /^Vehicle \+ estimated freight to / : /^Vehicle price only\./;
      if (!(context.ready ? context.ready === s.displayedBasis : ready.test(context.note || '')) || parseUSD(context.totalText) !== s.displayedTotalMinor) return null;
      return { minor: s.displayedTotalMinor, basis: s.displayedBasis };
    } catch { return null; }
  }

  return Object.freeze({ POLICY, normalize, validMinor, validCode, parseUSD, countryCode, hydrate, select, destination, resolve, validateRates, freshness, convert, format, readQuote });
});
